/**
 * The event-screen films (Gordon 2026-11-03: "lots of different films showing
 * the different parts of BB, looping all evening in random order … focus FOR
 * MUSIC MAKERS and every aspect and tool in the app"). Data only: every text
 * is `screens.<film>.<key>` in messages/*.json, every picture is real BB
 * content or a drawn tool (`mocks.tsx`). Only claim what BB does today; a
 * film about something not built yet says so on screen ("register").
 */

import type { MockId } from "./mocks";

export type Backdrop = "covers" | "people";
/** Ready-made strings a mock needs (prices in the viewer's currency …). */
export type LinesFor = "prices" | "ticket" | "meter" | "chat" | "shotsRelease" | "shotsMarket" | "shotsSync" | "surveyUrl" | "joinUrl" | "ledger";

export interface FilmBeat {
  ms: number;
  /** Headline key. */
  h: string;
  /** Pill keys under the headline. */
  pills?: string[];
  mock?: MockId;
  lines?: LinesFor;
  bg?: Backdrop;
}

export interface Film {
  id: string;
  /** Where its words live; default `screens.<id>`. */
  ns?: string;
  beats: FilmBeat[];
}

export const FILMS: Film[] = [
  { id: "release", beats: [
    { ms: 6500, h: "h1", mock: "wave" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3", "p4"], mock: "screens", lines: "shotsRelease" },
    { ms: 6000, h: "h3", bg: "covers" },
  ] },
  { id: "sell", beats: [
    { ms: 6500, h: "h1", mock: "price", lines: "prices" },
    { ms: 6500, h: "h2", pills: ["p1", "p2"], bg: "people" },
    { ms: 6500, h: "h3", mock: "screens", lines: "shotsMarket" },
    { ms: 6000, h: "h4" },
  ] },
  { id: "splits", beats: [
    { ms: 6500, h: "h1", mock: "faces" },
    { ms: 7000, h: "h2", mock: "donut" },
    { ms: 6500, h: "h3", pills: ["p1", "p2", "p3", "p4"] },
  ] },
  { id: "studio", beats: [
    { ms: 7500, h: "h1", pills: ["p1", "p2", "p3", "p4", "p5", "p6"], mock: "rec" },
    { ms: 6500, h: "h2", bg: "people" },
    { ms: 6000, h: "h3", mock: "wave" },
  ] },
  { id: "dj", beats: [
    { ms: 6500, h: "h1", mock: "decks" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "decks" },
    { ms: 6000, h: "h3", bg: "covers" },
  ] },
  { id: "epx", beats: [
    { ms: 6500, h: "h1", mock: "pads" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "pads" },
  ] },
  { id: "film", beats: [
    { ms: 6500, h: "h1", mock: "timeline" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3", "p4"], mock: "film" },
    { ms: 6000, h: "h3", mock: "timeline" },
  ] },
  { id: "artwork", beats: [
    { ms: 6500, h: "h1", mock: "layers" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "layers" },
  ] },
  { id: "master", beats: [
    { ms: 6500, h: "h1", mock: "meter", lines: "meter" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3", "p4", "p5"], mock: "wave" },
    { ms: 6000, h: "h3", mock: "meter", lines: "meter" },
  ] },
  { id: "score", beats: [
    { ms: 7000, h: "h1", mock: "staff" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3", "p4"], mock: "keys" },
    { ms: 5500, h: "h3" },
  ] },
  { id: "sync", beats: [
    { ms: 6500, h: "h1", mock: "film" },
    { ms: 6500, h: "h2", mock: "screens", lines: "shotsSync" },
    { ms: 7000, h: "h3", pills: ["p1", "p2", "p3"] },
  ] },
  { id: "collab", beats: [
    { ms: 6500, h: "h1", mock: "faces" },
    { ms: 7500, h: "h2", pills: ["p1", "p2", "p3", "p4", "p5", "p6"] },
    { ms: 6000, h: "h3", mock: "wave" },
  ] },
  { id: "academy", beats: [
    { ms: 6500, h: "h1", mock: "keys" },
    { ms: 6500, h: "h2", mock: "staff" },
    { ms: 7000, h: "h3", pills: ["p1", "p2", "p3", "p4"] },
  ] },
  { id: "copi", beats: [
    { ms: 8000, h: "h1", mock: "chat", lines: "chat" },
    { ms: 6500, h: "h2", mock: "donut" },
    { ms: 6000, h: "h3", pills: ["p1", "p2", "p3", "p4"] },
  ] },
  { id: "business", beats: [
    { ms: 6500, h: "h1", mock: "doc" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "doc" },
    { ms: 7000, h: "h3", pills: ["p4", "p5"] },
  ] },
  { id: "events", beats: [
    { ms: 6500, h: "h1", mock: "ticket", lines: "ticket" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "ticket", lines: "ticket" },
  ] },
  // Ask the room (Gordon 2026-11-03: a survey "especially for the event").
  { id: "survey", beats: [
    { ms: 9000, h: "h1", pills: ["p1", "p2", "p3"], mock: "qr", lines: "surveyUrl" },
    { ms: 8000, h: "h2", mock: "qr", lines: "surveyUrl" },
  ] },
  { id: "register", beats: [
    { ms: 7000, h: "h1", mock: "flow" },
    { ms: 7000, h: "h2", pills: ["p1", "p2", "p3"], mock: "flow" },
  ] },
];

/**
 * One film per kind of member (Gordon 2026-11-03: "more films on Story that
 * show all the tools … for the different user accounts: music maker,
 * producer, musician, sound engineer, photographer, film director, editor,
 * graphic designer, venue, manager …"). Words in `roleFilms.<role>`; each
 * ends on the shared join beat (`roleFilms.shared`) with a QR to sign up.
 */
export const ROLE_KEYS = [
  "artist", "producer", "musician", "songwriter", "engineer", "dj", "podcaster",
  "director", "editor", "photographer", "designer", "label", "manager", "venue",
] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

const JOIN: FilmBeat = { ms: 7000, h: "h4", pills: ["free", "own", "fee"], mock: "qr", lines: "joinUrl" };
const role = (key: RoleKey, b1: Partial<FilmBeat>, b2: Partial<FilmBeat>, b3: Partial<FilmBeat>): Film => ({
  id: `role-${key}`,
  ns: `roleFilms.${key}`,
  beats: [
    { ms: 6500, h: "h1", ...b1 },
    { ms: 7500, h: "h2", pills: ["p1", "p2", "p3", "p4"], ...b2 },
    { ms: 7500, h: "h3", ...b3 },
    JOIN,
  ],
});

export const ROLE_FILMS: Film[] = [
  role("artist", { mock: "wave" }, { mock: "price", lines: "prices" }, { pills: ["p5", "p6", "p7", "p8"], bg: "people" }),
  role("producer", { mock: "pads" }, { mock: "price", lines: "prices" }, { pills: ["p5", "p6", "p7", "p8"], mock: "meter", lines: "meter" }),
  role("musician", { mock: "faces" }, { mock: "rec" }, { pills: ["p5", "p6", "p7"], mock: "staff" }),
  role("songwriter", { mock: "keys" }, { mock: "staff" }, { pills: ["p5", "p6"], mock: "flow" }),
  role("engineer", { mock: "wave" }, { pills: ["p1", "p2", "p3", "p4", "p5"], mock: "meter", lines: "meter" }, { pills: ["p6", "p7", "p8", "p9"], mock: "donut" }),
  // Getting gigs through Book Talent — not the BB DJ app (Gordon 2026-11-03).
  role("dj", { mock: "calendar" }, { mock: "price", lines: "prices" }, { pills: ["p5", "p6", "p7", "p8"], mock: "wave" }),
  role("podcaster", { mock: "rec" }, { mock: "faces" }, { pills: ["p5", "p6", "p7", "p8"], mock: "timeline" }),
  role("director", { mock: "film" }, { pills: ["p1", "p2", "p3"], mock: "screens", lines: "shotsSync" }, { pills: ["p4", "p5", "p6", "p7"], mock: "timeline" }),
  role("editor", { mock: "timeline" }, { mock: "film" }, { pills: ["p5", "p6", "p7"], mock: "rec" }),
  role("photographer", { mock: "photos" }, { mock: "layers" }, { bg: "people" }),
  role("designer", {}, { mock: "layers" }, { pills: ["p5", "p6", "p7"], mock: "doc" }),
  role("label", { mock: "screens", lines: "shotsRelease" }, { mock: "donut" }, { pills: ["p5", "p6", "p7"], mock: "ledger", lines: "ledger" }),
  role("manager", { mock: "doc" }, { mock: "donut" }, { pills: ["p5", "p6", "p7"], mock: "ledger", lines: "ledger" }),
  role("venue", { bg: "people" }, { mock: "ticket", lines: "ticket" }, { pills: ["p5", "p6", "p7"], mock: "calendar" }),
];

/** Which role films fit a member, best first, from their profile roles. */
const ROLE_MATCH: [RoleKey, RegExp][] = [
  ["label", /^(label|a&r)$/i],
  ["manager", /manager/i],
  ["engineer", /engineer|mixing|mastering/i],
  ["producer", /producer|beat ?maker|remixer|programmer/i],
  ["songwriter", /songwriter|composer|lyricist|writer|arranger/i],
  ["dj", /^(dj|turntablist)$/i],
  ["musician", /musician|instrumentalist|performer|vocal|singer|conductor/i],
  ["director", /video director|co-director|film director|cinematographer|director of photography/i],
  ["editor", /editor|colorist|vfx|motion graphics/i],
  ["photographer", /photographer/i],
  ["designer", /designer|artwork|illustrator|art director/i],
  ["podcaster", /podcast|host/i],
  ["artist", /artist|rapper|^mc$|emcee|creator/i],
];
export function roleFilmsFor(roles: string[] = [], accountType?: string | null): RoleKey[] {
  const out: RoleKey[] = [];
  if (accountType?.toLowerCase() === "venue") out.push("venue");
  for (const [key, re] of ROLE_MATCH) {
    if (!out.includes(key) && roles.some(r => re.test(r.trim()))) out.push(key);
  }
  return out;
}

/** "why" is the 16:9 Why BB story (components/why-story), played whole. */
export const ALL_FILM_IDS = ["why", ...FILMS.map(f => f.id), ...ROLE_FILMS.map(f => f.id)];
export const filmById = (id: string) => FILMS.find(f => f.id === id) ?? ROLE_FILMS.find(f => f.id === id);
